import { createSelector } from "@reduxjs/toolkit";

import { CreateOrganizationNamespace } from "./constants";

import type { Organization } from "@packages/client";
import type { RootState } from "Store/configureStore";

export const organizationStateSelector = (state: RootState) =>
  state.organization;

export const organizationLoadingSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.loading
);

export const orgByDescriptionFieldSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.orgByDescriptionField
);

export const getOrganizations = createSelector(
  orgByDescriptionFieldSelector,
  orgByDescriptionField => Object.values(orgByDescriptionField)
);

export const organizationByDescriptionIdSelector = createSelector(
  orgByDescriptionFieldSelector,
  (_: RootState, params: { organizationDescriptionId?: string }) => params,
  (orgByDescriptionField, { organizationDescriptionId }) =>
    organizationDescriptionId
      ? orgByDescriptionField?.[organizationDescriptionId]
      : undefined
);

export const organizationsSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.data
);

export const organizationSelector = createSelector(
  organizationsSelector,
  (_: RootState, params: { organizationId?: string }) => params,
  (organizationData, { organizationId }) =>
    organizationId ? organizationData?.[organizationId] : undefined
);

export const organizationsMultiVendorOrgsSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.multiVendorOrgs
);

export const organizationListSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.list
);

export const organizationErrorsSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.errors
);

export const organizationInflightSelector = createSelector(
  organizationStateSelector,
  (_: RootState, params: { organizationId: string }) => params,
  (organizationState, { organizationId }) =>
    organizationState?.inflight?.[organizationId]
);

export const organization_ErrorsSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?._errors
);

export const organization_ErrorSelector = createSelector(
  organization_ErrorsSelector,
  (_: RootState, params: { organizationId: string }) => params,
  (_errors, { organizationId }) => _errors?.[organizationId]
);

export const createdOrganizationSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.[CreateOrganizationNamespace]
);

export const organizationLoadingErrorSelector = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.organizationLoadingError
);

export const selectDeletedOrganizations = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.deleted
);

export const deletedOrganizationSelector = createSelector(
  selectDeletedOrganizations,
  (_: RootState, params: { organizationId: string }) => params,
  (deletedOrganizations, { organizationId }) =>
    deletedOrganizations?.[organizationId]
);

export const isLoadingDeleteOrganizationSelector = createSelector(
  selectDeletedOrganizations,
  deletedOrganizations => deletedOrganizations?.loading
);

export const selectOrgEstimates = createSelector(
  organizationStateSelector,
  organizationState => organizationState?.organizationEstimate
);

export const organizationEstimateSelector = createSelector(
  selectOrgEstimates,
  (_: RootState, params: { organizationId: string }) => params.organizationId,
  (organizationEstimate, organizationId) =>
    organizationEstimate?.[organizationId]
);

export const organizationEstimateSubscriptionSelector = createSelector(
  organizationEstimateSelector,
  (_: RootState, params: { subscriptionId: string }) => params,
  (estimate, { subscriptionId }) =>
    estimate?.current.subscriptions?.list?.find(
      subscription => `${subscription.license_id}` === subscriptionId
    )
);

export const organizationsMemberOrOwnerOfSelector = createSelector(
  organizationListSelector,
  getOrganizations,
  (organizationList, organizations) =>
    organizations.filter(
      (organization): organization is Organization =>
        !!organizationList.find(id => organization?.id === id)
    )
);

export const canCreateProjectOrganizationsSelector = createSelector(
  getOrganizations,
  organizations =>
    organizations.filter(
      (organization): organization is Organization =>
        !!organization?.hasLink("create-subscription")
    )
);
